// B2B API Helper Functions

const API_BASE = '/api/b2b';

// Get token from localStorage - checks multiple possible keys
function getToken() {
  // Try b2b_session (object with access_token)
  const session = localStorage.getItem('b2b_session');
  if (session) {
    try {
      const parsed = JSON.parse(session);
      if (parsed.access_token) return parsed.access_token;
      if (parsed.session?.access_token) return parsed.session.access_token;
    } catch (e) {
      // If it's not JSON, maybe it's the token directly
      if (session.length > 20) return session;
    }
  }
  
  // Try b2b_token (plain token string)
  const token = localStorage.getItem('b2b_token');
  if (token) return token;
  
  // Try airgati_b2b_session
  const altSession = localStorage.getItem('airgati_b2b_session');
  if (altSession) {
    try {
      const parsed = JSON.parse(altSession);
      if (parsed.access_token) return parsed.access_token;
      if (parsed.session?.access_token) return parsed.session.access_token;
    } catch (e) {
      if (altSession.length > 20) return altSession;
    }
  }
  
  return null;
}

// API request helper
async function apiRequest(endpoint, options = {}) {
  const token = getToken();
  
  const headers = {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` }),
    ...options.headers
  };

  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'API request failed');
  }

  return data;
}

// ─────────────────────────────────────────────────────────────────
// AUTH
// ─────────────────────────────────────────────────────────────────

export async function login(email, password) {
  return apiRequest('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password })
  });
}

export async function signup(data) {
  return apiRequest('/auth/signup', {
    method: 'POST',
    body: JSON.stringify(data)
  });
}

export async function getMe() {
  return apiRequest('/auth/me');
}

// ─────────────────────────────────────────────────────────────────
// AGENCY SETTINGS
// ─────────────────────────────────────────────────────────────────

export async function getAgencySettings() {
  return apiRequest('/agency/settings');
}

export async function updateAgencySettings(updates) {
  console.log('Updating agency settings with:', updates); // Debug log
  
  return apiRequest('/agency/settings', {
    method: 'PATCH',
    body: JSON.stringify(updates)
  });
}

// ─────────────────────────────────────────────────────────────────
// LEADS
// ─────────────────────────────────────────────────────────────────

export async function getLeads(params = {}) {
  const queryString = new URLSearchParams(params).toString();
  return apiRequest(`/leads${queryString ? `?${queryString}` : ''}`);
}

export async function getLead(id) {
  return apiRequest(`/leads/${id}`);
}

export async function updateLead(id, updates) {
  return apiRequest(`/leads/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(updates)
  });
}

// ─────────────────────────────────────────────────────────────────
// ANALYTICS
// ─────────────────────────────────────────────────────────────────

export async function getAnalytics(params = {}) {
  const queryString = new URLSearchParams(params).toString();
  return apiRequest(`/analytics${queryString ? `?${queryString}` : ''}`);
}

// ─────────────────────────────────────────────────────────────────
// CONVERSATIONS
// ─────────────────────────────────────────────────────────────────

export async function getConversations(params = {}) {
  const queryString = new URLSearchParams(params).toString();
  return apiRequest(`/conversations${queryString ? `?${queryString}` : ''}`);
}

export async function getConversation(id) {
  return apiRequest(`/conversations/${id}`);
}

// ─────────────────────────────────────────────────────────────────
// FORMATTING HELPERS
// ─────────────────────────────────────────────────────────────────

export function formatDate(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
}

export function formatDateTime(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
}

export function formatRelativeTime(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return formatDate(dateString);
}

// Alias for formatRelativeTime
export const formatTimeAgo = formatRelativeTime;

export function formatCurrency(amount) {
  if (!amount && amount !== 0) return '';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0
  }).format(amount);
}

export function getInitials(name) {
  if (!name) return '?';
  return name
    .split(' ')
    .map(word => word[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

export function getScoreClass(score) {
  if (score >= 80) return 'high';
  if (score >= 50) return 'medium';
  return 'low';
}

export function getStatusColor(status) {
  const colors = {
    new: '#3b82f6',
    contacted: '#f59e0b',
    qualified: '#8b5cf6',
    booked: '#10b981',
    lost: '#6b7280'
  };
  return colors[status] || '#6b7280';
}
